import { TOPIC_GUIDES, type TopicGuide } from "./topic-guides";

export const BRIDGING_VISA_VEVO_GUIDE = {
  topicId: "bridging-visa-vevo",
  locale: "ko",
  publicationStatus: "korean_test_preview",
  risk: "R4",
  checkedAt: "2026-07-17",
  jurisdictionLabel: "호주 연방 이민법·개인 grant 조건",
  summary:
    "브리징비자는 이름만으로 권리를 판단할 수 없습니다. substantive visa와 bridging visa grant notice, VEVO의 현재 in-effect visa, 발효·종료 event, work·travel 조건을 한 timeline으로 확인하세요. VEVO에 bridging visa가 안 보여도 substantive visa가 아직 유효해서일 수 있으며, BVA·BVC·BVE로 출국하면 재입국이 불가능하거나 visa가 종료될 수 있습니다. 여행·퇴사·근무 시작·review 또는 judicial review 전에 등록 migration agent나 Australian legal practitioner의 긴급 검토를 받으세요.",
  keyPoints: [
    "모든 grant notice, application receipt, review 문서와 VEVO 결과를 날짜별로 저장하세요.",
    "VEVO는 현재 in-effect visa만 보여 주므로 미래에 발효될 bridging visa가 안 보일 수 있습니다.",
    "work rights는 bridging visa 종류가 아니라 실제 grant condition과 VEVO로 확인하세요.",
    "BVA·BVC·BVE는 일반적으로 출국 후 같은 visa로 돌아올 수 없으므로 출국 전에 확인하세요.",
    "BVB는 grant와 지정 travel period를 확인하기 전 출국하지 마세요.",
    "passport 변경 뒤 VEVO 오류가 나면 passport update와 reference number를 먼저 점검하세요.",
    "valid visa가 없거나 BVE 상태이면 Status Resolution Service와 독립 법률 조언을 즉시 확인하세요.",
  ],
  sections: [
    {
      id: "build-status-timeline",
      title: "1. grant notice와 모든 신청·review를 한 timeline으로 모으세요",
      body: [
        "현재 substantive visa의 grant notice, bridging visa notice, 새 visa 신청 receipt, refusal·review·judicial review 문서를 모아 application date, current visa expiry, bridging visa grant와 activation event를 적으세요.",
        "bridging visa가 grant됐다는 사실과 지금 in effect라는 사실은 다를 수 있습니다. 각 notice의 발효·종료 문구와 현재 VEVO를 대조하고 불명확하면 추측하지 말고 전문 확인을 받으세요.",
      ],
      checklist: ["현재·이전 visa grant notice", "TRN·application receipt", "review·court 문서와 기한", "bridging visa activation·cessation event", "최신 VEVO PDF 또는 screenshot"],
      sourceIds: ["bridging-vevo-source-overview", "bridging-vevo-source-bva", "bridging-vevo-source-bvc"],
    },
    {
      id: "read-vevo-correctly",
      title: "2. VEVO에는 현재 발효 중인 비자만 표시된다는 점을 확인하세요",
      body: [
        "VEVO에서 visa subclass, expiry 또는 must-not-arrive-after, period of stay와 conditions를 저장하세요. VEVO는 previous visa나 처리 중 application 전체를 보여 주는 시스템이 아닙니다.",
        "bridging visa notice가 있는데 VEVO에 안 보이면 substantive visa가 아직 in effect인지 먼저 확인하세요. passport·이름·reference number 오류이면 ImmiAccount 기록을 확인하고 공식 VEVO enquiry를 사용하세요.",
      ],
      checklist: ["VEVO의 current in-effect visa", "expiry·stay period·conditions", "substantive visa가 아직 유효한지", "passport와 Home Affairs 기록 일치", "VEVO error 화면·reference number"],
      sourceIds: ["bridging-vevo-source-overview", "bridging-vevo-source-enquiry"],
    },
    {
      id: "check-work-rights",
      title: "3. 근무 전 실제 work condition을 확인하세요",
      body: [
        "BVA·BVC·BVE 모두 개인별 grant condition에 따라 근무 가능 여부가 달라질 수 있습니다. 고용주 설명이나 이전 visa 조건을 그대로 적용하지 말고 current VEVO와 grant letter를 확인하세요.",
        "근로 제한 변경을 요청할 수 있는 상황과 financial hardship evidence는 visa 종류와 review 상태에 따라 다릅니다. 새 application을 중복 제출하지 말고 공식 경로와 필요한 form·증빙을 먼저 확인하세요.",
      ],
      checklist: ["현재 work condition number", "허용 시간·고용 형태", "financial hardship evidence", "review·judicial review가 조건변경에 미치는 영향", "정확한 ImmiAccount·webform 경로"],
      sourceIds: ["bridging-vevo-source-bva", "bridging-vevo-source-bvc", "bridging-vevo-source-bve", "bridging-vevo-source-application"],
    },
    {
      id: "do-not-travel-on-assumption",
      title: "4. BVB grant와 travel period 없이 출국하지 마세요",
      body: [
        "BVA가 in effect일 때 출국하면 그 BVA로 돌아올 수 없습니다. BVC·BVE도 출국 시 종료되고 같은 visa로 재입국할 수 없습니다. 항공권이나 긴급 사유가 있어도 출국 전 재입국 권리를 별도로 확인하세요.",
        "BVB는 지정된 travel period 동안 출국·재입국을 허용할 수 있지만 신청 receipt만으로 충분하지 않습니다. grant notice, travel period, passport와 다른 visa condition을 확인한 뒤 이동하세요.",
      ],
      checklist: ["현재 visa가 in effect인지", "BVB grant 여부", "travel period 시작·종료", "passport 유효성", "귀국일과 substantive application 상태", "해외 체류 중 연락·결정 수신 계획"],
      sourceIds: ["bridging-vevo-source-bva", "bridging-vevo-source-bvb", "bridging-vevo-source-bvc", "bridging-vevo-source-bve"],
    },
    {
      id: "resolve-errors-or-unlawful-status",
      title: "5. VEVO 오류·visa 종료·무비자 상태를 즉시 분리 대응하세요",
      body: [
        "VEVO technical error라면 passport update, grant number·TRN과 입력값을 확인하고 VEVO enquiry form을 사용하세요. 단순 화면 오류인지 실제 visa expiry·cessation인지 구분하기 전 일하거나 출국하지 마세요.",
        "valid visa가 없거나 BVE를 보유했다면 Status Resolution Service의 대상과 BVE 신청 경로를 확인하세요. review나 court application이 자동으로 lawful status를 만든다고 가정하지 말고 긴급 법률 조언을 받으세요.",
      ],
      checklist: ["technical error와 actual status 문제 구분", "visa expiry·cessation event", "VEVO enquiry 제출 기록", "BVE·Status Resolution 대상 여부", "긴급 migration legal advice"],
      sourceIds: ["bridging-vevo-source-enquiry", "bridging-vevo-source-status-resolution", "bridging-vevo-source-bve"],
    },
  ],
  faqs: [
    { id: "bridging-not-visible", question: "bridging visa grant가 있는데 VEVO에 안 보여요.", answer: "substantive visa가 아직 in effect이면 미래에 발효될 bridging visa가 표시되지 않을 수 있습니다. grant notice의 activation event와 current VEVO를 함께 확인하세요.", sourceIds: ["bridging-vevo-source-overview"] },
    { id: "bva-travel", question: "BVA로 해외여행 후 돌아올 수 있나요?", answer: "일반적으로 BVA가 in effect인 상태로 출국하면 같은 BVA로 재입국할 수 없습니다. 출국 전에 BVB가 실제 grant됐는지 확인하세요.", sourceIds: ["bridging-vevo-source-bva", "bridging-vevo-source-bvb"] },
    { id: "bvb-applied", question: "BVB를 신청했으니 바로 출국해도 되나요?", answer: "신청 receipt만으로 재입국 권리가 생기지 않습니다. BVB grant와 지정 travel period를 확인하기 전 출국하지 마세요.", sourceIds: ["bridging-vevo-source-bvb"] },
    { id: "bvc-bvb", question: "BVC 보유자도 BVB를 받을 수 있나요?", answer: "Home Affairs는 BVC 보유자에게 BVB를 grant할 수 없다고 안내합니다. 출국 전 재입국 가능한 다른 visa가 있는지 전문 확인이 필요합니다.", sourceIds: ["bridging-vevo-source-bvc"] },
    { id: "work-rights", question: "모든 bridging visa는 일할 수 있나요?", answer: "아닙니다. 실제 work condition은 grant letter와 current VEVO로 확인해야 합니다.", sourceIds: ["bridging-vevo-source-bva", "bridging-vevo-source-bvc", "bridging-vevo-source-bve"] },
    { id: "vary-work", question: "work restriction을 없애 달라고 신청할 수 있나요?", answer: "가능 여부와 증빙은 visa 종류·review 상태·financial hardship에 따라 다릅니다. 공식 신청 경로와 현재 요건을 확인하세요.", sourceIds: ["bridging-vevo-source-application", "bridging-vevo-source-bva", "bridging-vevo-source-bvc"] },
    { id: "vevo-error", question: "VEVO에 error가 뜹니다.", answer: "passport·reference number와 Home Affairs 기록을 먼저 점검하고 error 화면을 보관해 공식 VEVO enquiry를 사용하세요.", sourceIds: ["bridging-vevo-source-enquiry"] },
    { id: "new-passport", question: "여권을 갱신한 뒤 VEVO가 안 됩니다.", answer: "새 passport details가 Department 기록에 반영됐는지 확인하세요. 변경 전 여권과 grant number·TRN도 함께 준비하세요.", sourceIds: ["bridging-vevo-source-enquiry", "bridging-vevo-source-overview"] },
    { id: "no-valid-visa", question: "visa가 만료돼 현재 valid visa가 없는 것 같습니다.", answer: "즉시 VEVO와 notice를 확인하고 Status Resolution Service·BVE 경로와 독립 법률 조언을 받으세요.", sourceIds: ["bridging-vevo-source-status-resolution", "bridging-vevo-source-bve"] },
    { id: "duplicate-application", question: "처리가 늦어 같은 bridging 신청을 다시 내도 되나요?", answer: "공식 webform은 중복 신청이 지연을 늘릴 수 있다고 안내합니다. 기존 접수와 추가자료 경로를 먼저 확인하세요.", sourceIds: ["bridging-vevo-source-application"] },
  ],
  sourceIds: [
    "bridging-vevo-source-overview",
    "bridging-vevo-source-bva",
    "bridging-vevo-source-bvb",
    "bridging-vevo-source-bvc",
    "bridging-vevo-source-bve",
    "bridging-vevo-source-application",
    "bridging-vevo-source-enquiry",
    "bridging-vevo-source-status-resolution",
  ],
  additionalChecks: [
    "모든 행동 직전 current VEVO와 최신 grant notice를 다시 저장하세요.",
    "출국 전 BVB grant와 travel period를 registered migration agent 또는 lawyer와 확인하세요.",
    "work condition 변경은 visa 종류·review 상태와 최신 official form을 확인하세요.",
    "visa expiry·cancellation·review 이후 lawful status를 자동으로 가정하지 마세요.",
    "valid visa가 없거나 BVE이면 Status Resolution Service와 긴급 법률 조언을 확인하세요.",
  ],
  disclaimer:
    "이 콘텐츠는 브리징비자와 VEVO 사용에 관한 R4 테스트 미리보기 일반정보이며 이민 법률자문이 아닙니다. 특정 bridging visa의 발효, work·travel 권리, condition variation, lawful status 또는 재입국을 보장하지 않습니다. 출국·근무·review·court action 전 Home Affairs의 최신 notice·VEVO와 등록 migration agent 또는 Australian legal practitioner의 조언을 확인하세요.",
} as const satisfies TopicGuide;

const mutableGuides = TOPIC_GUIDES as unknown as TopicGuide[];
if (!mutableGuides.some((guide) => guide.topicId === BRIDGING_VISA_VEVO_GUIDE.topicId)) {
  mutableGuides.push(BRIDGING_VISA_VEVO_GUIDE);
}
